import styled from '@emotion/native';
import { GroupSecurity } from '@quromedical/fhir-common';
import type { ArrayFilter } from '@quromedical/utils';
import { compact } from '@quromedical/utils';
import {
  BORDER_RADIUS,
  fadeColor,
  MARGIN,
  useTheme,
} from 'design-system/theme';
import { router } from 'expo-router';
import { logger } from 'helpers';
import { useUserSession } from 'hooks';
import { useActiveAssignedAdmissions } from 'hooks/useActiveAssignedAdmissions';
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  KBarSearch,
  useKBar,
  useMatches,
  useRegisterActions,
} from 'kbar';
import type React from 'react';
import { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { strings } from 'strings';

import { getSearchStyles, SearchItem } from './components';
import { getAdmissionAction, getPatientActions } from './patient';
import type {
  Action,
  UseKBarPatientActionsHook,
  UseKBarToggleHook,
} from './types';

const isNotHidden: ArrayFilter<Action> = (action) => !action.hidden;

const createPermissionActionFilter =
  (permissions: GroupSecurity[]): ArrayFilter<Action> =>
  (action) => {
    if (!action.permission) {
      return false;
    }

    return permissions.includes(action.permission);
  };

const Provider: React.FC<PropsWithChildren> = ({ children }) => {
  const session = useUserSession();
  const permissions = useMemo(() => session.getPermissions(), [session]);

  const { data: admissions, isLoading } = useActiveAssignedAdmissions({});

  const admissionActionOptions = useMemo(
    () => admissions?.map<Action | undefined>(getAdmissionAction),
    [admissions],
  );
  const admissionActions = useMemo(
    () => compact(admissionActionOptions).filter(isNotHidden),
    [admissionActionOptions],
  );

  const patientActions = useMemo(
    () =>
      admissionActions
        .map((admissionAction) =>
          getPatientActions({
            parentId: admissionAction.id,
            patientId: admissionAction.id,
            patientName: admissionAction.name,
          }),
        )
        .flat(),
    [admissionActions],
  );

  const actions: Action[] = useMemo(
    () => [
      {
        id: 'patient',
        permission: GroupSecurity.PatientRead,
        name: strings.KBarActiveAdmissionsTitle,
        subtitle: strings.KBarActiveAdmissionsSubtitle,
        iconName: 'person-outline',
      },
      {
        id: 'home',
        permission: GroupSecurity.FeaturePreviewNavigator,
        name: strings.KBarHomeTitle,
        shortcut: ['g', 'h'],
        subtitle: strings.KBarHomeSubtitle,
        iconName: 'home',
        perform: () =>
          router.push({
            pathname: '/',
          }),
      },
      {
        id: 'dashboard',
        permission: GroupSecurity.PatientRead,
        name: strings.KBarDashboardTitle,
        shortcut: ['g', 'd'],
        subtitle: strings.KBarDashboardSubtitle,
        iconName: 'dashboard',
        perform: () =>
          router.push({
            pathname: '/dashboard',
          }),
      },
      {
        id: 'patients',
        permission: GroupSecurity.PatientRead,
        name: strings.KBarPatientTitle,
        shortcut: ['g', 'p'],
        subtitle: strings.KBarPatientSubtitle,
        iconName: 'people-outline',
        perform: () =>
          router.push({
            pathname: '/patient/patients',
          }),
      },
      {
        id: 'admissions',
        permission: GroupSecurity.EncounterRead,
        name: strings.KBarAdmissionTitle,
        shortcut: ['g', 'a'],
        subtitle: strings.KBarAdmissionSubtitle,
        iconName: 'schedule',
        perform: () =>
          router.push({
            pathname: '/patient/admissions',
          }),
      },
      {
        id: 'admin-onboard-medical-aid',
        permission: GroupSecurity.PatientCreate,
        name: strings.KBarAdminOnboardMedicalAidTitle,
        subtitle: strings.KBarAdminOnboardMedicalAidSubtitle,
        shortcut: ['o', 'm'],
        iconName: 'person-add-alt',
        perform: () =>
          router.push({
            pathname: '/admin/onboard/medical-aid',
          }),
      },
      {
        id: 'admin-onboard-private',
        permission: GroupSecurity.PatientCreate,
        name: strings.KBarAdminOnboardPrivateTitle,
        subtitle: strings.KBarAdminOnboardPrivateSubtitle,
        shortcut: ['o', 'p'],
        iconName: 'person-add-alt',
        perform: () =>
          router.push({
            pathname: '/admin/onboard/private',
          }),
      },
      ...admissionActions,
      ...patientActions,
    ],
    [admissionActions, patientActions],
  );

  const hasPermissions = useCallback(
    () => createPermissionActionFilter(permissions),
    [permissions],
  );
  const filteredActions = actions.filter(hasPermissions).filter(isNotHidden);

  const providerActions = useMemo(
    () => (isLoading ? actions : filteredActions),
    [filteredActions, isLoading, actions],
  );

  return (
    <KBarProvider actions={providerActions} key={providerActions.length}>
      {children}
    </KBarProvider>
  );
};

const MainWrapper = styled.View(({ theme }) => {
  const { width } = useWindowDimensions();

  useEffect(() => {
    logger.track({
      type: 'gamification',
      name: 'kbar-search-render',
    });
  }, []);

  return {
    backgroundColor: fadeColor(theme.color['base-grey'], 0.95),
    borderRadius: BORDER_RADIUS[4],
    width: width / 2,
    maxWidth: 800,
    shadowColor: theme.color.black,
    shadowOpacity: 0.2,
    shadowRadius: 10,
  };
});

const SearchInputWrapper = styled.View({
  marginVertical: MARGIN.xs,
  marginHorizontal: MARGIN.s,
});

export const Content: React.FC = () => {
  const matches = useMatches();
  const { height } = useWindowDimensions();
  const maxHeight = height / 2;

  const theme = useTheme();
  const searchStyles = getSearchStyles(theme.color['text-default']);

  return (
    <KBarPortal>
      <KBarPositioner>
        <KBarAnimator>
          <MainWrapper>
            <SearchInputWrapper>
              <KBarSearch style={searchStyles} />
            </SearchInputWrapper>
            <KBarResults
              items={matches.results}
              maxHeight={maxHeight}
              onRender={({ item, active }) => (
                <SearchItem item={item} active={active} />
              )}
            />
          </MainWrapper>
        </KBarAnimator>
      </KBarPositioner>
    </KBarPortal>
  );
};

export const KBar: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Provider>
      <Content />

      {children}
    </Provider>
  );
};

/**
 * Will add actions for the current patient to the actions list and will remove on dismount
 */
export const useKBarPatientActions: UseKBarPatientActionsHook = (
  patientId,
  patientName,
) => {
  const { query } = useKBar();

  const currentPatientAction: Action = {
    id: 'current-patient',
    name: patientName,
    subtitle: strings.KBarCurrentPatientActionTitleSubtitle,
    iconName: 'account-circle',
    priority: 2,
  };

  const patientActions = getPatientActions({
    parentId: currentPatientAction.id,
    patientId,
    patientName,
    priority: 2,
  });

  const actions = [currentPatientAction, ...patientActions];

  // make the actions dependant query as well since the query may not be defined on initial mount
  useRegisterActions(query ? actions : [], [patientId, patientName, query]);
};

export const useToggle: UseKBarToggleHook = () => {
  const { query } = useKBar();

  // To handle the case where the KBar is not initially mounted
  if (!query?.toggle) {
    return () => undefined;
  }

  return query.toggle;
};
